import {
  BorrowerOnboardingDetails,
  Loan,
  OnboardingDetailsRole,
  patchUpdateBorrowerDetails,
} from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { CheckoutSessionDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/entities/CheckoutSessionDetailEntity';
import { useCheckoutSession } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useCheckoutSession';
import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import { useBorrowerOnboardingDetails } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/hooks/useBorrowerOnboardingDetails';
import { StoredBorrowerOnboardingDetails } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/types';
import { patchNieDataIfRequired } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/utils/patchNieDataIfRequired';
import { MerchantPos } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPos';
import { AddParticipantToLoanDialog } from '@/components/widgets/LoanDetailActions/components/LoanDetailActionsDialog/components/AddParticipantToLoanDialog';

interface AddCoborrowerDialogProps {
  paymentMethodDetailEntity: LoanPaymentMethodDetailEntity;
  opened: boolean;
  setOpened: (opened: boolean) => void;
}

type AddCoBorrowerDto = Parameters<typeof patchUpdateBorrowerDetails>[0];

export const AddCoborrowerDialog = ({ paymentMethodDetailEntity, opened, setOpened }: AddCoborrowerDialogProps) => {
  const { checkoutSessionEntity } = useCheckoutSession();

  const loan = paymentMethodDetailEntity.loan;

  if (!loan) {
    return null;
  }

  const paymentIntentId = paymentMethodDetailEntity.paymentIntentId;
  const borrowerOnboardingDetails = loan.onboardingDetails.find(
    (participant) => participant.role === OnboardingDetailsRole.BORROWER,
  );

  const selectedRole =
    loan.onboardingDetails.length < 3
      ? loan.onboardingDetails.some((borrower) => borrower.role === OnboardingDetailsRole.CO_BORROWER)
        ? OnboardingDetailsRole.GUARANTOR
        : OnboardingDetailsRole.CO_BORROWER
      : undefined;

  if (!selectedRole || !paymentIntentId || !checkoutSessionEntity || !borrowerOnboardingDetails) {
    return null;
  }

  return (
    <AddCoborrowerDialogContent
      paymentMethodDetailEntity={paymentMethodDetailEntity}
      loan={loan}
      selectedRole={selectedRole}
      paymentIntentId={paymentIntentId}
      checkoutSessionEntity={checkoutSessionEntity}
      borrowerOnboardingDetails={borrowerOnboardingDetails as BorrowerOnboardingDetails}
      opened={opened}
      setOpened={setOpened}
    />
  );
};

interface AddCoborrowerDialogContentProps {
  paymentMethodDetailEntity: LoanPaymentMethodDetailEntity;
  loan: Loan;
  selectedRole: OnboardingDetailsRole;
  paymentIntentId: string;
  checkoutSessionEntity: CheckoutSessionDetailEntity;
  borrowerOnboardingDetails: BorrowerOnboardingDetails;
  opened: boolean;
  setOpened: (opened: boolean) => void;
}

const AddCoborrowerDialogContent = ({
  paymentMethodDetailEntity,
  loan,
  selectedRole,
  paymentIntentId,
  checkoutSessionEntity,
  borrowerOnboardingDetails,
  opened,
  setOpened,
}: AddCoborrowerDialogContentProps) => {
  const { refreshCheckoutSessionAndPaymentMethodEntities } = useCheckoutSession();
  const { borrowerOnboardingDetailsWithIncome } = useBorrowerOnboardingDetails({
    loan,
    borrowerOnboardingDetails,
    role: OnboardingDetailsRole.BORROWER,
  });

  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (error) {
      setError(null);
      throw error;
    }
  }, [error]);

  const isMerchantPos = paymentMethodDetailEntity.isMerchantPos();

  const onSaveAddCoborrower = async (onboardingDetails: StoredBorrowerOnboardingDetails) => {
    const borrowerOnboardingDetailsId = uuidv4();

    if (!onboardingDetails) {
      throw new Error(`Borrower onboarding details for role ${selectedRole} are required`);
    }

    try {
      const addCoborrowerDto: AddCoBorrowerDto = {
        ...onboardingDetails,
        id: checkoutSessionEntity.id,
        paymentIntentId: paymentIntentId,
        onboardingDetailsId: borrowerOnboardingDetailsId,
        role: selectedRole,
      };

      await patchUpdateBorrowerDetails(addCoborrowerDto);
      await patchNieDataIfRequired({
        onboardingDetails,
        onboardingDetailsId: borrowerOnboardingDetailsId,
        paymentMethodDetailEntity,
      });
      await refreshCheckoutSessionAndPaymentMethodEntities();
      setOpened(false);
    } catch (caughtError) {
      setError(caughtError as Error);
    }
  };

  const handleClose = () => setOpened(false);

  if (isMerchantPos) {
    if (!borrowerOnboardingDetailsWithIncome) {
      return null;
    }

    return (
      <MerchantPos
        opened={opened}
        setOpened={setOpened}
        paymentMethodDetailEntity={paymentMethodDetailEntity}
        checkoutSessionDetailEntity={checkoutSessionEntity}
        selectedRole={selectedRole}
        onSaveDetails={onSaveAddCoborrower}
        borrowerOnboardingDetails={[borrowerOnboardingDetailsWithIncome]}
      />
    );
  }

  return <AddParticipantToLoanDialog loan={loan} opened={opened} setOpened={setOpened} handleClose={handleClose} />;
};
